"use client";

// THE HALF THE ERROR BOUNDARIES CANNOT SEE.
//
// A React error boundary catches errors thrown while RENDERING the tree beneath
// it, and nothing else. It does not see:
//
//   • an error thrown in an event handler — a click, a change, a submit. React
//     deliberately lets those propagate to the window instead.
//   • a rejected promise nobody awaited — a fetch in an effect, an autosave.
//   • anything thrown from setTimeout / setInterval / requestAnimationFrame.
//   • a chunk that fails to load on a lazy route.
//
// Every one of those is a crash the user sees (or a control that silently stops
// working) and none of them reach app/error.tsx. So the two window-level
// listeners here are not belt-and-braces — they are most of the coverage, and
// the boundaries are the part that also gets to draw something.
//
// ⚠️ ONE THING NEITHER HALF CAN CATCH, and it is worth being honest about: an
// error that happens BEFORE this module runs — the main bundle failing to parse
// or to download at all. There is no JavaScript answer to "the JavaScript did
// not load"; that one shows up as a blank page and has to be found in the
// access log. Everything after hydration is covered.
//
// Renders nothing, and is mounted OUTSIDE the signed-in branch of the layout on
// purpose: /signin and /login/continue are pages too, and a first-login crash is
// exactly the report we most want.

import { useEffect } from "react";
import { reportClientError } from "@/lib/client-error-report";
import { reloadIfStale } from "@/lib/stale-build";

export function ClientErrorReporter() {
  useEffect(() => {
    const onError = (e: ErrorEvent) => {
      // `error` is the thrown value; on a cross-origin script error the browser
      // scrubs it and leaves only the message, which is still worth having.
      reportClientError({ error: e.error ?? e.message, source: "window" });
      // A CHUNK THAT 404s LANDS HERE, NOT IN A BOUNDARY. After a deploy the new
      // build has different chunk filenames, so a stale tab loading one late —
      // a lazy route, a dynamic import — fails outside React's reach entirely.
      // Same recovery, same once-per-build guard.
      void reloadIfStale();
    };
    const onRejection = (e: PromiseRejectionEvent) => {
      reportClientError({ error: e.reason, source: "unhandledrejection" });
      void reloadIfStale();
    };

    window.addEventListener("error", onError);
    window.addEventListener("unhandledrejection", onRejection);
    return () => {
      window.removeEventListener("error", onError);
      window.removeEventListener("unhandledrejection", onRejection);
    };
  }, []);

  return null;
}
